import React, { useState, useRef, useEffect, useCallback } from 'react';
import logo from '../logo.png'; 

const API_BASE = '/api';

const SPORTS_LIST = [
  { id: 'soccer', name: 'Soccer', icon: '⚽' },
  { id: 'basketball', name: 'Basketball', icon: '🏀' },
  { id: 'tennis', name: 'Tennis', icon: '🎾' },
  { id: 'baseball', name: 'Baseball', icon: '⚾' },
  { id: 'american_football', name: 'Football', icon: '🏈' },
  { id: 'golf', name: 'Golf', icon: '⛳' }
];

const RANDOM_TEMPLATES = ['drunk_dance', 'hot_feet_dance', 'bouncy_dance', 'pumpitup_dance'];

export default function VideoCreator() {
  const [step, setStep] = useState('email'); 
  const [history, setHistory] = useState([]); 
  
  const [email, setEmail] = useState('');
  const [sport, setSport] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); 
  const [photoData, setPhotoData] = useState(null); 
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const goToStep = useCallback((nextStep) => {
    setHistory(prev => [...prev, step]);
    setStep(nextStep);
  }, [step]);

  const goBack = () => {
    setHistory(prev => {
      const newHistory = [...prev];
      const previousStep = newHistory.pop();
      setStep(previousStep);
      return newHistory;
    });
  };

  // Camera Initialization
  useEffect(() => {
    if (step === 'camera') {
      const initCamera = async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
        }
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } } 
          });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
              videoRef.current.play().catch(e => console.error("Play blocked:", e));
            };
          }
        } catch (err) { alert("Camera error: " + err); }
      };
      initCamera();
    }
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, [step, facingMode]);

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  // Countdown Logic
  const startCountdown = () => {
    setCountdown(3);
  };

  useEffect(() => {
    if (step !== 'camera') {
      setCountdown(null);
    }
  }, [step]);

  const takePhoto = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;

    let targetWidth, targetHeight;
    const targetRatio = 3 / 4;
    const videoRatio = video.videoWidth / video.videoHeight;

    if (videoRatio > targetRatio) {
      targetHeight = video.videoHeight;
      targetWidth = targetHeight * targetRatio;
    } else {
      targetWidth = video.videoWidth;
      targetHeight = targetWidth / targetRatio;
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    const startX = (video.videoWidth - targetWidth) / 2;
    const startY = (video.videoHeight - targetHeight) / 2;

    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, startX, startY, targetWidth, targetHeight, 0, 0, targetWidth, targetHeight);
    setPhotoData(canvas.toDataURL('image/jpeg'));
    goToStep('preview');
  }, [facingMode, goToStep]);

  // 2. Safe execution of the timer
  useEffect(() => {
    if (countdown === null) return;

    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      if (step === 'camera') {
        takePhoto();
      }
      setCountdown(null);
    }
  }, [countdown, step, takePhoto]);

  const submitJob = async () => {
    setStep('processing');
    setError(null);
    
    const randomTemplate = RANDOM_TEMPLATES[Math.floor(Math.random() * RANDOM_TEMPLATES.length)];
    
    try {
      const payload = {
        image: photoData, 
        sport: sport,
        user_email: email,
        prompt_video: randomTemplate 
      };
      const response = await fetch(`${API_BASE}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Server error");

      setHistory([]);
      setStep('thankyou');
    } catch (err) { 
      setStep('preview'); 
      setError("Failed to start generation."); 
    }
  };

  return (
    <div className="app-wrapper">
      {history.length > 0 && !['processing', 'thankyou'].includes(step) && (
        <button className="btn-back" onClick={goBack}>
          &larr; Back
        </button>
      )}

      {step === 'email' && (
        <div className="screen">
          <img src={logo} alt="Pixperience" className="logo" />
          <h1>Ready for the Big Leagues?</h1>
          <p className="subtitle">Step into your ultimate sports moment and receive your personalized video straight to your inbox.</p>
          <input type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" />
          <button className="btn" disabled={!email || !email.includes('@')} onClick={() => goToStep('sport')}>Get Started &rarr;</button>
        </div>
      )}

      {step === 'sport' && (
        <div className="screen">
          <h1>Choose Your Sport</h1>
          <div className="sport-grid">
            {SPORTS_LIST.map(s => (
              <div key={s.id} onClick={() => setSport(s.id)} className={`sport-card ${sport === s.id ? 'selected' : ''}`}>
                <div className="sport-icon">{s.icon}</div>
                <div className="sport-name">{s.name}</div>
              </div>
            ))}
          </div>
          <button className="btn" disabled={!sport} onClick={() => goToStep('camera')}>Continue &rarr;</button>
        </div>
      )}

      {step === 'camera' && (
        <div className="screen">
          <h1>Strike a Pose!</h1>
          <p className="subtitle">Frame yourself and capture.</p>
          <div className="media-container">
            <video ref={videoRef} autoPlay playsInline muted className="media-element" style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}></video>
            
            {/* UPDATED: Added key={countdown} to force CSS animation restart */}
            {countdown !== null && countdown > 0 && (
              <div key={countdown} className="countdown-overlay">
                {countdown}
              </div>
            )}
          </div>
          <canvas ref={canvasRef} style={{display: 'none'}}></canvas>
          <button className="btn btn-secondary" onClick={toggleCamera} disabled={countdown !== null}>Flip Camera</button>
          <button className="btn" onClick={startCountdown} disabled={countdown !== null}>
            {countdown !== null ? "Get Ready!" : "Capture Photo 📸"}
          </button>
        </div>
      )}

      {step === 'preview' && (
        <div className="screen">
            <h1>Looking Good!</h1>
            <p className="subtitle">Happy with your photo?</p>
            <div className="media-container">
              <img src={photoData} alt="Preview" className="media-element" />
            </div>
            {error && <p className="error-text">{error}</p>}
            <button className="btn btn-secondary" onClick={goBack}>Retake</button>
            <button className="btn" onClick={submitJob}>Create My Video! 🚀</button>
        </div>
      )}

      {step === 'processing' && (
        <div className="screen">
          <div className="loader"></div>
          <h1>Creating...</h1>
          <p className="subtitle">We're transforming you into a professional athlete. This will take a few minutes.</p>
        </div>
      )}

      {step === 'thankyou' && (
        <div className="screen">
          <div className="emoji-huge">🎉</div>
          <h1>All Done!</h1>
          <p className="subtitle" style={{marginBottom: '15px'}}>Your professional sports video will be sent to:</p>
          <div className="result-box">{email}</div>
          <p className="subtitle" style={{fontSize: '14px', marginTop: '15px'}}>Check your inbox in the next few minutes.</p>
          <button className="btn" onClick={() => {
            setStep('email'); setEmail(''); setSport(null); setPhotoData(null);
          }}>Create Another Video</button>
        </div>
      )}
    </div>
  );
}